import * as THREE from 'three';

export class InputController {
  private pressedKeys: Set<string>;
  private mouseDelta: { x: number; y: number };
  private wheelDelta: number;
  private isPointerLocked: boolean;
  private movementTouchStartPos: { x: number; y: number } | null;
  private movementTouchCurrentPos: { x: number; y: number } | null;
  private cameraTouchStartPos: { x: number; y: number } | null;
  private cameraTouchCurrentPos: { x: number; y: number } | null;
  private _isMobile: boolean;
  private movementJoystickElement: HTMLElement | null;
  private cameraJoystickElement: HTMLElement | null;
  private actionButton: HTMLElement | null;
  private isActionPressed: boolean;
  private keys: { [key: string]: boolean } = {};
  private mousePosition: { x: number, y: number } = { x: 0, y: 0 };
  private mouseButtons: { [button: number]: boolean } = {};
  private touchState: { [id: number]: { x: number, y: number, startX: number, startY: number } } = {};
  public enabled: boolean = true;
  private isTyping: boolean = false;
  private movementKeys: string[] = ['KeyW', 'KeyA', 'KeyS', 'KeyD', 'Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

  constructor() {
    this.pressedKeys = new Set();
    this.mouseDelta = { x: 0, y: 0 };
    this.wheelDelta = 0;
    this.isPointerLocked = false;
    this.movementTouchStartPos = null;
    this.movementTouchCurrentPos = null;
    this.cameraTouchStartPos = null;
    this.cameraTouchCurrentPos = null;
    this._isMobile = this.checkIfMobile();
    this.movementJoystickElement = document.getElementById('movement-joystick');
    this.cameraJoystickElement = document.getElementById('camera-joystick');
    this.actionButton = document.getElementById('action-button');
    this.isActionPressed = false;

    // Remove any existing debug element
    const existingDebugElement = document.getElementById('joystick-debug');
    if (existingDebugElement && existingDebugElement.parentNode) {
      existingDebugElement.parentNode.removeChild(existingDebugElement);
    }

    // Setup event listeners
    this.setupKeyboardListeners();
    this.setupMouseListeners();
    this.setupInputFocusListeners();
    if (this._isMobile) {
      this.setupTouchListeners();
      this.setupOrientationChangeListener();
      this.ensureMobileControlsVisible();
    }
  }

  public get isMobile(): boolean {
    return this._isMobile;
  }

  private checkIfMobile(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  private setupKeyboardListeners(): void {
    document.addEventListener('keydown', (event) => {
      if (!this.enabled) return;
      
      // If typing in chat, only allow Escape and Enter keys
      if (this.isTyping && this.movementKeys.includes(event.code)) {
        return;
      }
      
      this.pressedKeys.add(event.code);
      this.keys[event.code] = true;
    });

    document.addEventListener('keyup', (event) => {
      if (!this.enabled) return;
      this.pressedKeys.delete(event.code);
      this.keys[event.code] = false;
    });
  }

  private setupMouseListeners(): void {
    document.addEventListener('mousemove', (event) => {
      if (this.isPointerLocked) {
        this.mouseDelta.x = event.movementX;
        this.mouseDelta.y = event.movementY;
      }
    });

    document.addEventListener('wheel', (event) => {
      this.wheelDelta = event.deltaY;
    });

    document.addEventListener('pointerlockchange', () => {
      this.isPointerLocked = document.pointerLockElement !== null;
    });
  }

  private setupTouchListeners(): void {
    if (!this.movementJoystickElement || !this.cameraJoystickElement || !this.actionButton) return;

    const movementArea = this.movementJoystickElement.parentElement;
    const cameraArea = this.cameraJoystickElement.parentElement;

    if (!movementArea || !cameraArea) return;

    // Movement joystick touch handling
    movementArea.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];
      const rect = movementArea.getBoundingClientRect();
      this.movementTouchStartPos = {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
      this.movementTouchCurrentPos = { ...this.movementTouchStartPos };
    });

    movementArea.addEventListener('touchmove', (event) => {
      event.preventDefault();
      if (!this.movementTouchStartPos || !this.movementJoystickElement) return;

      const touch = event.touches[0];
      const rect = movementArea.getBoundingClientRect();
      this.movementTouchCurrentPos = {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };

      this.updateJoystickPosition(
        this.movementJoystickElement,
        this.movementTouchStartPos,
        this.movementTouchCurrentPos
      );
    });

    movementArea.addEventListener('touchend', () => {
      this.movementTouchStartPos = null;
      this.movementTouchCurrentPos = null;
      if (this.movementJoystickElement) {
        this.movementJoystickElement.style.left = '50%';
        this.movementJoystickElement.style.top = '50%';
        this.movementJoystickElement.style.transform = 'translate(-50%, -50%)';
      }
    });

    // Camera joystick touch handling
    cameraArea.addEventListener('touchstart', (event) => {
      event.preventDefault();
      const touch = event.touches[0];
      const rect = cameraArea.getBoundingClientRect();
      this.cameraTouchStartPos = {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
      this.cameraTouchCurrentPos = { ...this.cameraTouchStartPos };
    });

    cameraArea.addEventListener('touchmove', (event) => {
      event.preventDefault();
      if (!this.cameraTouchStartPos || !this.cameraJoystickElement) return;

      const touch = event.touches[0];
      const rect = cameraArea.getBoundingClientRect();
      this.cameraTouchCurrentPos = {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };

      this.updateJoystickPosition(
        this.cameraJoystickElement,
        this.cameraTouchStartPos,
        this.cameraTouchCurrentPos
      );
    });

    cameraArea.addEventListener('touchend', () => {
      this.cameraTouchStartPos = null;
      this.cameraTouchCurrentPos = null;
      if (this.cameraJoystickElement) {
        this.cameraJoystickElement.style.left = '50%';
        this.cameraJoystickElement.style.top = '50%';
        this.cameraJoystickElement.style.transform = 'translate(-50%, -50%)';
      }
    });

    // Action button touch handling
    this.actionButton.addEventListener('touchstart', () => {
      this.isActionPressed = true;
      this.pressedKeys.add('KeyE');
      
      // Provide visual feedback
      if (this.actionButton) {
        this.actionButton.style.backgroundColor = 'rgba(100, 200, 255, 0.6)';
        this.actionButton.style.transform = 'translateX(-50%) scale(0.9)';
      }
      
      // Call the global interaction handler from main.ts
      if (typeof window.handleInteraction === 'function') {
        window.handleInteraction();
      } else {
        // Fallback to simulating E key press
        const eKeyEvent = new KeyboardEvent('keydown', {
          key: 'e',
          code: 'KeyE',
          bubbles: true,
          cancelable: true
        });
        document.dispatchEvent(eKeyEvent);
      }
    });

    this.actionButton.addEventListener('touchend', () => {
      this.isActionPressed = false;
      this.pressedKeys.delete('KeyE');
      
      // Reset visual feedback
      if (this.actionButton) {
        this.actionButton.style.backgroundColor = 'rgba(100, 200, 255, 0.3)';
        this.actionButton.style.transform = 'translateX(-50%)';
      }
    });
  }

  private updateJoystickPosition(joystickElement: HTMLElement, startPos: { x: number; y: number }, currentPos: { x: number; y: number }): void {
    const deltaX = currentPos.x - startPos.x;
    const deltaY = currentPos.y - startPos.y;
    
    // Get joystick dimensions for proper positioning
    const joystickWidth = joystickElement.offsetWidth;
    const joystickRadius = joystickWidth / 2;
    const maxRadius = 50; // Maximum movement radius
    
    // Calculate distance and angle
    const distance = Math.min(maxRadius, Math.sqrt(deltaX * deltaX + deltaY * deltaY));
    const angle = Math.atan2(deltaY, deltaX);

    // Calculate position with the joystick's actual dimensions
    const joystickX = startPos.x + distance * Math.cos(angle) - joystickRadius;
    const joystickY = startPos.y + distance * Math.sin(angle) - joystickRadius;

    joystickElement.style.left = `${joystickX}px`;
    joystickElement.style.top = `${joystickY}px`;
    joystickElement.style.transform = 'none';
  }

  public isKeyPressed(key: string): boolean {
    if (!this.enabled) return false;
    
    // If typing in chat, don't allow movement keys
    if (this.isTyping && this.movementKeys.includes(key)) {
      return false;
    }
    
    if (this._isMobile && this.movementTouchCurrentPos && this.movementTouchStartPos) {
      // Convert joystick position to WASD keys
      const deltaX = this.movementTouchCurrentPos.x - this.movementTouchStartPos.x;
      const deltaY = this.movementTouchCurrentPos.y - this.movementTouchStartPos.y;
      
      // Adjust threshold based on screen orientation
      const isPortrait = window.innerHeight > window.innerWidth;
      const threshold = isPortrait ? 10 : 8; // Lower threshold values for more sensitivity
      
      switch (key) {
        case 'KeyW':
          return deltaY < -threshold;
        case 'KeyS':
          return deltaY > threshold;
        case 'KeyA':
          return deltaX < -threshold;
        case 'KeyD':
          return deltaX > threshold;
        case 'KeyE':
          return this.isActionPressed;
        default:
          return this.pressedKeys.has(key);
      }
    }
    return this.keys[key] === true || this.pressedKeys.has(key);
  }

  private calculateJoystickDelta(currentPos: { x: number; y: number }, startPos: { x: number; y: number }, maxRadius: number = 50): { x: number; y: number } {
    const deltaX = currentPos.x - startPos.x;
    const deltaY = currentPos.y - startPos.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // Determine the actual max radius based on screen orientation
    const isPortrait = window.innerHeight > window.innerWidth;
    const actualMaxRadius = isPortrait ? 30 : 25; // Smaller radius for more sensitivity
    
    // Normalize the delta values between -1 and 1
    const normalizedX = distance > 0 ? (deltaX / distance) * Math.min(distance, actualMaxRadius) / actualMaxRadius : 0;
    const normalizedY = distance > 0 ? (deltaY / distance) * Math.min(distance, actualMaxRadius) / actualMaxRadius : 0;
    
    return { x: normalizedX, y: normalizedY };
  }

  public getMouseDelta(): { x: number; y: number } {
    if (!this.enabled) return { x: 0, y: 0 };
    if (this._isMobile && this.cameraTouchCurrentPos && this.cameraTouchStartPos) {
      // Calculate normalized delta and apply higher sensitivity for mobile
      const normalizedDelta = this.calculateJoystickDelta(
        this.cameraTouchCurrentPos,
        this.cameraTouchStartPos
      );
      
      // Apply higher sensitivity for mobile camera movement
      // Use different sensitivity based on orientation
      const isPortrait = window.innerHeight > window.innerWidth;
      const sensitivity = isPortrait ? 40 : 50; // Increased sensitivity values
      
      return {
        x: normalizedDelta.x * sensitivity,
        y: normalizedDelta.y * sensitivity
      };
    }
    
    const delta = { ...this.mouseDelta };
    this.mouseDelta = { x: 0, y: 0 };
    return delta;
  }

  public getWheelDelta(): number {
    const delta = this.wheelDelta;
    this.wheelDelta = 0;
    return delta;
  }

  public requestPointerLock(): void {
    if (!this._isMobile) {
      const canvas = document.querySelector('canvas');
      canvas?.requestPointerLock();
    }
  }

  public getPointerLockState(): boolean {
    return this._isMobile || this.isPointerLocked;
  }

  private updateDebugInfo(): void {
    // Remove this method's implementation since we're not using the debug element
  }

  private setupOrientationChangeListener(): void {
    // Listen for orientation changes
    window.addEventListener('orientationchange', () => {
      // Reset joystick positions
      if (this.movementJoystickElement) {
        this.movementJoystickElement.style.left = '50%';
        this.movementJoystickElement.style.top = '50%';
        this.movementJoystickElement.style.transform = 'translate(-50%, -50%)';
      }
      
      if (this.cameraJoystickElement) {
        this.cameraJoystickElement.style.left = '50%';
        this.cameraJoystickElement.style.top = '50%';
        this.cameraJoystickElement.style.transform = 'translate(-50%, -50%)';
      }
      
      // Reset touch positions
      this.movementTouchStartPos = null;
      this.movementTouchCurrentPos = null;
      this.cameraTouchStartPos = null;
      this.cameraTouchCurrentPos = null;
      
      // Force mobile controls to be visible
      const mobileControls = document.querySelector('.mobile-controls');
      if (mobileControls) {
        mobileControls.setAttribute('style', 'display: block !important');
      }
    });
    
    // Also listen for resize events which can happen when orientation changes
    window.addEventListener('resize', () => {
      // Same reset logic as above
      if (this.movementJoystickElement) {
        this.movementJoystickElement.style.left = '50%';
        this.movementJoystickElement.style.top = '50%';
        this.movementJoystickElement.style.transform = 'translate(-50%, -50%)';
      }
      
      if (this.cameraJoystickElement) {
        this.cameraJoystickElement.style.left = '50%';
        this.cameraJoystickElement.style.top = '50%';
        this.cameraJoystickElement.style.transform = 'translate(-50%, -50%)';
      }
      
      // Force mobile controls to be visible on resize (which happens on orientation change)
      if (this._isMobile) {
        const mobileControls = document.querySelector('.mobile-controls');
        if (mobileControls) {
          mobileControls.setAttribute('style', 'display: block !important');
        }
      }
    });
  }

  private ensureMobileControlsVisible(): void {
    // Make sure mobile controls are visible, especially in landscape mode
    setTimeout(() => {
      const mobileControls = document.querySelector('.mobile-controls');
      if (mobileControls) {
        mobileControls.setAttribute('style', 'display: block !important');
        
        // Log to console for debugging
        console.log('Mobile controls visibility enforced');
        console.log('Is landscape:', window.innerWidth > window.innerHeight);
        console.log('Window dimensions:', window.innerWidth, 'x', window.innerHeight);
      }
    }, 500); // Small delay to ensure DOM is ready
  }

  // Setup listeners for input focus to detect when user is typing in chat
  private setupInputFocusListeners(): void {
    // Monitor all focus events on the document
    document.addEventListener('focusin', (event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        console.log('Input focused, disabling movement keys');
        this.isTyping = true;
      }
    });

    document.addEventListener('focusout', (event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        console.log('Input blurred, enabling movement keys');
        this.isTyping = false;
      }
    });
    
    // Specifically look for the chat input from ChatUI
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const chatInput = document.querySelector('input[placeholder*="Press Enter to type"]') as HTMLInputElement;
          if (chatInput && !this.isListeningToChat(chatInput)) {
            this.addChatInputListeners(chatInput);
          }
        }
      });
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
    
    // Check if chat input already exists
    setTimeout(() => {
      const chatInput = document.querySelector('input[placeholder*="Press Enter to type"]') as HTMLInputElement;
      if (chatInput) {
        this.addChatInputListeners(chatInput);
      }
    }, 1000);
  }
  
  private isListeningToChat(element: HTMLElement): boolean {
    return element.getAttribute('data-input-monitored') === 'true';
  }
  
  private addChatInputListeners(chatInput: HTMLInputElement): void {
    if (!chatInput) return;
    
    console.log('Adding listeners to chat input');
    
    // Mark this input as being monitored
    chatInput.setAttribute('data-input-monitored', 'true');
    
    chatInput.addEventListener('focus', () => {
      console.log('Chat input focused');
      this.isTyping = true;
    });
    
    chatInput.addEventListener('blur', () => {
      console.log('Chat input blurred');
      this.isTyping = false;
    });
  }

  // Helper method to manually set typing state
  public setTypingState(isTyping: boolean): void {
    this.isTyping = isTyping;
  }
  
  // Helper method to check typing state
  public getTypingState(): boolean {
    return this.isTyping;
  }
} 